<script lang="ts" setup>
import StudentGallery from "@/components/StudentPage/StudentGallery.vue"
import StudentIndex from "@/components/StudentPage/StudentIndex.vue"
import {
  PageEvent,
  StudentPageState,
  useStudentPageTransitions,
  resetStudentPageState,
} from "@/composables/use-student-page-transitions"
import { KirbyBlock } from "@/queries/blocks"
import { useSpring } from "@/composables/use-spring"
import { useHeaderHeight } from "@/composables/use-header-height"
import { useMotion } from "@vueuse/motion"
import { ref, watch, onMounted, onUnmounted } from "vue"
import { useZoom } from "@/composables/use-zoom"
import StudentZoom from "./StudentZoom.vue"
import ZoomFlight from "../ZoomFlight.vue"
import SVG_Arrow from "../SVGs/SVG_Arrow.vue"

const { offsetHeader = false } = defineProps<{
  blocks: KirbyBlock[]
  offsetHeader?: boolean
}>()

const { pageMode, send, flightMounted, flightDirection, history } =
  useStudentPageTransitions()
// Reset before children mount — FlexSlideshow can emit `loaded` synchronously
// in its onMounted (no-image case), which fires before the parent's onMounted.
resetStudentPageState()
const { headerHeight } = useHeaderHeight()

const wrapper = ref<HTMLDivElement | null>(null)
const offset = ref<HTMLDivElement | null>(null)
const galleryEl = ref<HTMLDivElement | null>(null)
const galleryInner = ref<HTMLDivElement | null>(null)
const indexEl = ref<HTMLDivElement | null>(null)
const loadingEl = ref<HTMLDivElement | null>(null)

const getLoadingHeight = () => {
  if (typeof window === "undefined") return 160
  return parseFloat(getComputedStyle(document.documentElement).fontSize) * 10
}

const loadingHeight = ref(getLoadingHeight())

const { config } = useSpring("linear")
const { apply } = useMotion(wrapper, {
  initial: { height: loadingHeight.value },
})
const { apply: applyOffset } = useMotion(offset, {
  initial: { marginTop: 0 },
})

const collapsed = (mode: StudentPageState) =>
  mode === StudentPageState.ZOOM ||
  (mode === StudentPageState.ZOOMFLGHT && flightDirection.value === "in")

let offsetPrimed = false
const syncOffset = (mode: StudentPageState) => {
  if (!offsetHeader) return
  const marginTop = collapsed(mode) ? 0 : headerHeight.value
  applyOffset({
    marginTop,
    transition: offsetPrimed ? config : { ...config, duration: 0 },
  })
  offsetPrimed = true
}

watch(headerHeight, () => syncOffset(pageMode.value))

const { closeZoom, isZoomed, zoomData } = useZoom()
watch(isZoomed, zoomed => {
  if (zoomed) {
    send(PageEvent.OPEN_ZOOM)
  } else {
    send(PageEvent.CLOSE_ZOOM)
  }
})

const galleryRef = ref<InstanceType<typeof StudentGallery> | null>(null)
const gallerySlideIndex = ref(0)

function openSlide(index: number) {
  gallerySlideIndex.value = index
  send(PageEvent.CLOSE_INDEX)
}

async function onGalleryLoaded() {
  await new Promise(r => setTimeout(r, 1000)) // Wait for any layout shifts to finish
  send(PageEvent.GALLERY_LOADED)
}

function panelClass(state: StudentPageState) {
  return pageMode.value === state ? "" : "opacity-0 pointer-events-none"
}

const heights: Record<number, number> = {
  [StudentPageState.GALLERY]: 0,
  [StudentPageState.INDEX]: 0,
  [StudentPageState.LOADING]: loadingHeight.value,
}

watch(pageMode, mode => {
  syncOffset(mode)
  if (mode === StudentPageState.LOADING) {
    apply({ height: loadingHeight.value, transition: config })
    return
  }
  if (
    mode === StudentPageState.ZOOM ||
    (mode === StudentPageState.ZOOMFLGHT && flightDirection.value === "in")
  ) {
    apply({ height: window.innerHeight, transition: config })
    return
  }
  if (mode === StudentPageState.ZOOMFLGHT && flightDirection.value === "out") {
    const h = heights[history.value]
    if (h > 0) apply({ height: h, transition: config })
    return
  }
  const h = heights[mode]
  if (h > 0) apply({ height: h, transition: config })
})

let ro: ResizeObserver | null = null
let removeResizeListener: (() => void) | null = null

onMounted(() => {
  syncOffset(pageMode.value)
  const syncLoadingHeight = () => {
    loadingHeight.value = getLoadingHeight()
    heights[StudentPageState.LOADING] = loadingHeight.value
    if (pageMode.value === StudentPageState.LOADING) {
      apply({ height: loadingHeight.value, transition: config })
    }
  }
  syncLoadingHeight()
  window.addEventListener("resize", syncLoadingHeight)
  removeResizeListener = () =>
    window.removeEventListener("resize", syncLoadingHeight)

  const panels = [
    { state: StudentPageState.GALLERY, el: galleryEl.value },
    { state: StudentPageState.INDEX, el: indexEl.value },
  ]
  ro = new ResizeObserver(entries => {
    for (const entry of entries) {
      const panel = panels.find(({ el }) => el === entry.target)
      if (!panel) continue
      const h = entry.borderBoxSize[0].blockSize
      heights[panel.state] = h
      if (panel.state === pageMode.value) {
        apply({ height: h, transition: config })
      }
    }
  })
  panels.forEach(({ el }) => el && ro!.observe(el))
})

onUnmounted(() => {
  ro?.disconnect()
  removeResizeListener?.()
})

const toggleMode = (mode: StudentPageState) => {
  if (mode === pageMode.value) return
  send(
    mode === StudentPageState.GALLERY
      ? PageEvent.CLOSE_INDEX
      : PageEvent.OPEN_INDEX,
  )
}
</script>

<template>
  <div ref="offset">
    <div
      ref="wrapper"
      class="bg-gold relative overflow-hidden"
      :class="{
        'border-y': pageMode !== StudentPageState.LOADING,
      }"
    >
      <div
        v-if="
          blocks.length > 1 &&
          (pageMode === StudentPageState.GALLERY ||
            pageMode === StudentPageState.INDEX)
        "
        class="absolute top-3 right-3 p-4 py-2 border z-20 text-sm gap-3 hidden md:flex"
      >
        <button
          class="cursor-pointer md:hover:text-white"
          :class="{ underline: pageMode === StudentPageState.GALLERY }"
          @click="toggleMode(StudentPageState.GALLERY)"
        >
          Gallery
        </button>

        <button
          class="cursor-pointer md:hover:text-white"
          :class="{ underline: pageMode === StudentPageState.INDEX }"
          @click="toggleMode(StudentPageState.INDEX)"
        >
          Index
        </button>
      </div>
      <div
        class="absolute inset-0 z-10 panel-transition flex items-center justify-center"
        :class="panelClass(StudentPageState.LOADING)"
      ></div>
      <div class="relative h-full">
        <div
          ref="loadingEl"
          class="absolute top-0 left-0 w-full h-full panel-transition"
          :class="panelClass(StudentPageState.LOADING)"
        >
          <div
            class="min-h-40 h-full flex items-center justify-center text-center border-b small px-4 bg-white body-text text-black"
          >
            <div class="text-sm">Loading gallery...</div>
          </div>
        </div>
        <div
          ref="galleryEl"
          class="absolute top-0 left-0 w-full panel-transition"
          :class="panelClass(StudentPageState.GALLERY)"
        >
          <StudentGallery
            ref="galleryRef"
            :blocks="blocks"
            :initial-slide="gallerySlideIndex"
            @slide-change="gallerySlideIndex = $event"
            @loaded="onGalleryLoaded"
          />
        </div>
        <div
          ref="indexEl"
          class="absolute top-0 left-0 w-full panel-transition"
          :class="panelClass(StudentPageState.INDEX)"
        >
          <StudentIndex
            :blocks="blocks"
            :active="pageMode === StudentPageState.INDEX"
            @open="openSlide"
          />
        </div>
        <div
          class="absolute top-0 left-0 w-full"
          :class="panelClass(StudentPageState.ZOOM)"
        >
          <StudentZoom
            v-if="pageMode === StudentPageState.ZOOM"
            :data="zoomData"
            @close="closeZoom()"
          />
        </div>
      </div>
      <!-- <SVG_Arrow
        v-if="blocks.length > 1 && pageMode === StudentPageState.GALLERY"
        class="absolute bottom-3 hidden md:block right-2 w-1/8 z-20 stroke-black fill-white stroke-1 cursor-pointer -scale-y-100 md:hover:fill-black"
        @click="galleryRef?.slideNext()"
      />
      <SVG_Arrow
        v-if="blocks.length > 1 && pageMode === StudentPageState.GALLERY"
        class="absolute bottom-3 hidden md:block left-2 -scale-x-100 -scale-y-100 w-1/8 z-20 stroke-black stroke-1 fill-white cursor-pointer md:hover:fill-black"
        @click="galleryRef?.slidePrev()"
      /> -->
    </div>
  </div>
  <Teleport to="body">
    <ZoomFlight v-if="flightMounted" />
  </Teleport>
</template>

<style scoped>
.panel-transition {
  transition: opacity 300ms ease-in-out;
}
</style>
